import { Box, Button, CircularProgress, Paper, TextField, Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import FirebaseContext from '../context/firebase';
import NavLogo from '../images/NavLogo.svg';

type Mode = 'verifyEmail' | 'resetPassword' | 'recoverEmail' | 'signIn';
type Phase = 'working' | 'form' | 'success' | 'error';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(3),
      background: 'linear-gradient(90.07deg, #0088CF 0%, #00B7B2 100%)',
    },
    card: {
      maxWidth: 440,
      width: '100%',
      padding: theme.spacing(5, 4),
      borderRadius: 16,
      textAlign: 'center',
    },
    logo: { height: 44, marginBottom: theme.spacing(3) },
  })
);

const friendlyError = (e: any) => {
  switch (e?.code) {
    case 'auth/expired-action-code':
      return 'This link has expired. Please request a new one.';
    case 'auth/invalid-action-code':
      return 'This link is invalid or has already been used. Please request a new one.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'No account was found for this request.';
    case 'auth/weak-password':
      return 'Please choose a stronger password (at least 6 characters).';
    default:
      return e?.message || 'Something went wrong. Please try again.';
  }
};

/**
 * Branded handler for Firebase email action links (verify email, password reset,
 * email-change recovery). Replaces Firebase's default action pages. The one-time
 * oobCode is carried in the link and applied here via the JS SDK.
 */
const AuthAction = () => {
  const classes = useStyles();
  const { firebaseApp } = useContext(FirebaseContext);
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const mode = params.get('mode') as Mode | null;
  const oobCode = params.get('oobCode') || '';
  const continueUrl = params.get('continueUrl') || undefined;

  const [phase, setPhase] = useState<Phase>('working');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const auth = firebaseApp.auth();

  useEffect(() => {
    let active = true;
    const run = async () => {
      // Email-link sign-in (teammate invites). The Firebase custom action URL
      // sends every action link here, but sign-in is completed by the root
      // flow (NavBar/SignUpModal detect isSignInWithEmailLink), so forward
      // the untouched link params to '/' — the same redirect Firebase's own
      // hosted handler used to do.
      if (mode === 'signIn') {
        history.replace(`/${location.search}`);
        return;
      }
      if (!oobCode || !mode) {
        if (active) {
          setPhase('error');
          setTitle('Invalid link');
          setMessage('This link is missing required information. Please request a new email.');
        }
        return;
      }
      try {
        if (mode === 'verifyEmail') {
          await auth.applyActionCode(oobCode);
          if (active) {
            setPhase('success');
            setTitle('Email verified');
            setMessage('Your email address has been verified. You can now continue.');
          }
        } else if (mode === 'recoverEmail') {
          const info = await auth.checkActionCode(oobCode);
          await auth.applyActionCode(oobCode);
          if (active) {
            setPhase('success');
            setTitle('Email change reverted');
            setMessage(`Your sign-in email has been restored${info.data.email ? ` to ${info.data.email}` : ''}.`);
          }
        } else if (mode === 'resetPassword') {
          const emailForCode = await auth.verifyPasswordResetCode(oobCode);
          if (active) {
            setEmail(emailForCode);
            setTitle('Reset your password');
            setPhase('form');
          }
        } else {
          if (active) {
            setPhase('error');
            setTitle('Unsupported request');
            setMessage('This action is not supported.');
          }
        }
      } catch (e) {
        if (active) {
          setPhase('error');
          setTitle('Link expired or invalid');
          setMessage(friendlyError(e));
        }
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [auth, mode, oobCode, history, location.search]);

  const submitReset = async () => {
    setFormError('');
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await auth.confirmPasswordReset(oobCode, password);
      setPhase('success');
      setTitle('Password updated');
      setMessage('Your password has been changed. You can now log in with your new password.');
    } catch (e) {
      setFormError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const goContinue = () => {
    if (continueUrl && continueUrl.startsWith(window.location.origin)) {
      window.location.href = continueUrl;
    } else {
      history.push('/');
    }
  };

  return (
    <Box className={classes.root}>
      <Paper className={classes.card} elevation={6}>
        <img src={NavLogo} alt="Virtual Stack" className={classes.logo} />

        {phase === 'working' ? (
          <Box py={4}>
            <CircularProgress color="secondary" />
            <Typography mt={2} color="textSecondary">
              Processing…
            </Typography>
          </Box>
        ) : null}

        {phase === 'form' ? (
          <>
            <Typography variant="h5" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              for {email}
            </Typography>
            <TextField
              fullWidth
              type="password"
              label="New password"
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm password"
              margin="normal"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {formError ? (
              <Typography color="error" variant="body2" mt={1}>
                {formError}
              </Typography>
            ) : null}
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              sx={{ mt: 3 }}
              disabled={submitting}
              onClick={submitReset}
            >
              Update password
            </Button>
          </>
        ) : null}

        {phase === 'success' ? (
          <>
            <Typography variant="h5" gutterBottom color="secondary">
              {title}
            </Typography>
            <Typography variant="body1" color="textSecondary" mb={3}>
              {message}
            </Typography>
            <Button fullWidth variant="contained" color="secondary" onClick={goContinue}>
              Continue to Virtual Stack
            </Button>
          </>
        ) : null}

        {phase === 'error' ? (
          <>
            <Typography variant="h5" gutterBottom color="error">
              {title}
            </Typography>
            <Typography variant="body1" color="textSecondary" mb={3}>
              {message}
            </Typography>
            <Button fullWidth variant="outlined" color="secondary" onClick={() => history.push('/')}>
              Back to Virtual Stack
            </Button>
          </>
        ) : null}
      </Paper>
    </Box>
  );
};

export default AuthAction;
